/**
 * VideoContextPanel — visual scene descriptions from Gemini video analysis.
 *
 * Displays the timestamped visual context captured alongside the transcript
 * when a video file is processed with Gemini's native video understanding.
 * Each entry shows what was visible on screen at that moment — slide content,
 * shared screens, documents, etc.
 */

import type { VideoSceneDescription } from '../types';

interface VideoContextPanelProps {
  scenes: VideoSceneDescription[];
}

function formatTime(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function VideoContextPanel({ scenes }: VideoContextPanelProps) {
  if (!scenes.length) return null;

  const isNoVisuals =
    scenes.length === 1 &&
    /no significant visual/i.test(scenes[0].description);

  return (
    <div className="rounded-2xl border border-[#e2e8f0] bg-white overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-[#e2e8f0] bg-gradient-to-r from-[#f8fafc] to-white">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#00d4b8]/10">
          <svg className="w-4 h-4 text-[#00bfa8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
          </svg>
        </div>
        <div>
          <h3 className="text-sm font-bold text-[#0d1117]">Visual Context</h3>
          <p className="text-[11px] text-[#64748b]">
            {isNoVisuals
              ? 'No visual content detected'
              : `${scenes.length} scene${scenes.length !== 1 ? 's' : ''} captured by Gemini video analysis`}
          </p>
        </div>
        <span className="ml-auto text-[10px] font-semibold uppercase tracking-widest text-[#00bfa8] bg-[#00d4b8]/10 px-2 py-0.5 rounded-full">
          Gemini Video
        </span>
      </div>

      {isNoVisuals ? (
        <div className="px-5 py-6 text-center text-sm text-[#64748b]">
          <p>{scenes[0].description}</p>
        </div>
      ) : (
        <div className="divide-y divide-[#f1f5f9] max-h-80 overflow-y-auto">
          {scenes.map((scene, i) => (
            <div key={i} className="flex gap-3 px-5 py-3 hover:bg-[#f8fafc] transition-colors">
              <span className="shrink-0 font-mono text-[11px] font-bold text-[#00bfa8] bg-[#00d4b8]/10 px-2 py-0.5 rounded mt-0.5">
                {scene.timestamp || formatTime(scene.timestampSecs)}
              </span>
              <p className="text-[13px] text-[#374151] leading-relaxed">{scene.description}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
